import React, { useMemo, useRef, useLayoutEffect } from 'react';
import gsap from 'gsap';

const LINE_TL = '#f43f5e';
const LINE_SV = '#22c55e';
const FONT = 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const PLOT_MARGIN_LEFT = 40;
const PLOT_MARGIN_RIGHT = 22;

/** Connect every rated match to the *previous* rated match in time order, skipping gaps (no dead segments). */
function buildSeriesPath(points, valueKey, getY) {
  const segments = [];
  points.forEach((p) => {
    const v = p[valueKey];
    if (!Number.isFinite(v)) return;
    segments.push({ x: p.x, y: getY(v) });
  });
  if (segments.length === 0) return '';
  let d = `M ${segments[0].x} ${segments[0].y}`;
  for (let i = 1; i < segments.length; i += 1) {
    d += ` L ${segments[i].x} ${segments[i].y}`;
  }
  if (segments.length === 1) {
    d += ` L ${segments[0].x} ${segments[0].y}`;
  }
  return d;
}

/** Match-wise primary (red) + secondary (green) rating lines — defaults TL + SV; override labels for employee match vs average, etc.
 * @param {'both'|'tl'|'sv'} seriesMode — show both lines, primary only, or secondary only
 * @param {'light'|'dark'} variant — chart ink/grid for card background */
export function MatchRatingTrendChart({
  rows,
  fillSpace = false,
  seriesMode = 'both',
  variant = 'light',
  labelPrimary = 'TL',
  labelSecondary = 'SV',
}) {
  const wrapRef = useRef(null);
  const chartRootRef = useRef(null);
  const pathTlRef = useRef(null);
  const pathSvRef = useRef(null);
  const pointWrapRef = useRef(null);

  const showTl = seriesMode === 'both' || seriesMode === 'tl';
  const showSv = seriesMode === 'both' || seriesMode === 'sv';

  const chart = useMemo(() => {
    if (!rows?.length) return null;
    /** Min horizontal pixels per match so labels stay readable; chart scrolls horizontally when wider than the card. */
    const baseWidth = Math.max(360, rows.length * 72);
    const width = baseWidth;
    const height = 188;
    const margin = {
      top: 22,
      right: PLOT_MARGIN_RIGHT,
      bottom: 30,
      left: PLOT_MARGIN_LEFT,
    };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;
    const xInset = Math.min(24, innerWidth * 0.06);
    const minY = 0;
    const maxY = 5;
    const yTicks = [0, 1, 2, 3, 4, 5];
    const getX = (pointIndex) =>
      rows.length <= 1
        ? margin.left + innerWidth / 2
        : margin.left + xInset + (pointIndex * (innerWidth - xInset * 2)) / (rows.length - 1);
    const getY = (value) => margin.top + ((maxY - value) / (maxY - minY || 1)) * innerHeight;
    const points = rows.map((row, pointIndex) => {
      const tl = Number.isFinite(row.value) ? Number(row.value) : null;
      const sv = Number.isFinite(row.svValue) ? Number(row.svValue) : null;
      return {
        ...row,
        x: getX(pointIndex),
        tl,
        sv,
      };
    });

    const pathDtl = buildSeriesPath(points, 'tl', getY);
    const pathDsv = buildSeriesPath(points, 'sv', getY);

    return {
      width,
      height,
      margin,
      innerWidth,
      innerHeight,
      yTicks,
      getY,
      points,
      pathDtl,
      pathDsv,
      hasTl: Boolean(pathDtl),
      hasSv: Boolean(pathDsv),
    };
  }, [rows]);

  useLayoutEffect(() => {
    if (!chart || !wrapRef.current) return;

    const pathDuration = Math.min(1.45, 0.5 + chart.points.length * 0.11);
    const markerStart = pathDuration * 0.22;

    const ctx = gsap.context(() => {
      if (chartRootRef.current) {
        gsap.fromTo(chartRootRef.current, { opacity: 0 }, { opacity: 1, duration: 0.35, ease: 'power1.out' });
      }

      const lineTl = gsap.timeline({ defaults: { ease: 'power2.out' } });
      const elTl = pathTlRef.current;
      const elSv = pathSvRef.current;

      if (showTl && elTl && chart.pathDtl) {
        const lenTl = elTl.getTotalLength();
        if (lenTl > 0.5) {
          gsap.set(elTl, { strokeDasharray: lenTl, strokeDashoffset: lenTl });
          lineTl.to(elTl, { strokeDashoffset: 0, duration: pathDuration });
        }
      }

      if (showSv && elSv && chart.pathDsv) {
        const lenSv = elSv.getTotalLength();
        if (lenSv > 0.5) {
          gsap.set(elSv, { strokeDasharray: lenSv, strokeDashoffset: lenSv });
          lineTl.to(
            elSv,
            { strokeDashoffset: 0, duration: pathDuration },
            showTl && elTl && chart.pathDtl ? 0.1 : 0
          );
        }
      }

      const markers = pointWrapRef.current?.querySelectorAll('.trend-marker');
      const circles = pointWrapRef.current?.querySelectorAll('.trend-point-circle, .trend-sv-circle');
      const anyPath =
        (showTl && elTl && chart.pathDtl && elTl.getTotalLength() > 0.5) ||
        (showSv && elSv && chart.pathDsv && elSv.getTotalLength() > 0.5);

      if (anyPath && circles?.length) {
        circles.forEach((c) => gsap.set(c, { attr: { r: 0 } }));
        lineTl.to(
          circles,
          {
            attr: { r: 3.2 },
            duration: 0.36,
            stagger: { each: 0.055, from: 'start' },
            ease: 'back.out(1.35)',
          },
          markerStart
        );
      }
      if (markers?.length) {
        gsap.set(markers, { opacity: 0 });
        lineTl.to(
          markers,
          { opacity: 1, duration: 0.32, stagger: { each: 0.035, from: 'start' } },
          markerStart
        );
      }
    }, wrapRef);

    return () => ctx.revert();
  }, [chart, seriesMode]);

  useLayoutEffect(() => {
    if (!chart) return;
    const circles = pointWrapRef.current?.querySelectorAll('.trend-point-circle, .trend-sv-circle');
    if (!circles?.length) return;

    const cleanups = [];
    circles.forEach((circle) => {
      const onEnter = () => {
        gsap.to(circle, { attr: { r: 5.2 }, duration: 0.2, ease: 'power2.out' });
      };
      const onLeave = () => {
        gsap.to(circle, { attr: { r: 3.2 }, duration: 0.2, ease: 'power2.out' });
      };
      circle.addEventListener('mouseenter', onEnter);
      circle.addEventListener('mouseleave', onLeave);
      cleanups.push(() => {
        circle.removeEventListener('mouseenter', onEnter);
        circle.removeEventListener('mouseleave', onLeave);
      });
    });
    return () => cleanups.forEach((fn) => fn());
  }, [chart, seriesMode]);

  if (!chart) return null;

  const {
    width,
    height,
    margin,
    innerWidth,
    innerHeight,
    yTicks,
    getY,
    points,
    pathDtl,
    pathDsv,
    hasTl,
    hasSv,
  } = chart;

  const isLight = variant === 'light';

  /** In "both" mode TL/SV use distinct marker colors; in single-series mode markers match the line (red / green). */
  const tlPointFill = LINE_TL;
  const svPointFill = seriesMode === 'both' ? '#4ade80' : LINE_SV;
  const svValueLabelFill = isLight
    ? seriesMode === 'both'
      ? '#166534'
      : '#15803d'
    : seriesMode === 'both'
      ? '#bbf7d0'
      : '#a7f3d0';

  const tlValueFill = isLight ? '#0f172a' : '#ffffff';
  const gridStroke = isLight ? 'rgba(15, 23, 42, 0.08)' : 'rgba(255, 255, 255, 0.1)';
  const axisStroke = isLight ? '#94a3b8' : 'rgba(255, 255, 255, 0.9)';
  const tickFill = isLight ? '#475569' : 'rgba(255,255,255,0.88)';
  const matchLabelFill = isLight ? '#64748b' : 'rgba(255,255,255,0.82)';
  const markerRing = isLight ? '#cbd5e1' : '#ffffff';

  const svgCommonText = {
    fontFamily: FONT,
    style: { pointerEvents: 'none' },
    paintOrder: 'stroke fill',
  };

  const outerStyle = fillSpace
    ? {
        width: '100%',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }
    : { width: '100%' };

  const viewportStyle = fillSpace
    ? {
        position: 'relative',
        flex: 1,
        minHeight: height,
        width: '100%',
        overflowX: 'auto',
        overflowY: 'hidden',
        WebkitOverflowScrolling: 'touch',
        borderRadius: '8px',
        scrollbarGutter: 'stable',
      }
    : {
        overflow: 'hidden',
        width: '100%',
        minHeight: height,
        borderRadius: '10px',
      };

  const chartAreaStyle = fillSpace
    ? {
        position: 'relative',
        display: 'inline-block',
        minWidth: width,
        width,
        verticalAlign: 'top',
      }
    : { width: '100%' };

  const svgStyle = fillSpace
    ? {
        display: 'block',
        width,
        height,
        flexShrink: 0,
      }
    : {
        display: 'block',
        maxWidth: '100%',
      };

  const ariaGraph =
    seriesMode === 'both'
      ? `Match-wise ${labelPrimary} and ${labelSecondary} rating graph`
      : seriesMode === 'tl'
      ? `Match-wise ${labelPrimary} rating graph`
      : `Match-wise ${labelSecondary} rating graph`;

  return (
    <div ref={wrapRef} style={outerStyle}>
      <div style={viewportStyle}>
        <div ref={chartRootRef} style={chartAreaStyle}>
          <svg
            width={fillSpace ? width : '100%'}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            preserveAspectRatio="xMidYMid meet"
            role="img"
            aria-label={ariaGraph}
            shapeRendering="geometricPrecision"
            textRendering="geometricPrecision"
            style={svgStyle}
          >
            {yTicks.map((tick) => {
              const y = getY(tick);
              return (
                <g key={`y-${tick}`}>
                  <line
                    x1={margin.left}
                    y1={y}
                    x2={margin.left + innerWidth}
                    y2={y}
                    stroke={gridStroke}
                    strokeWidth="1"
                  />
                  <text
                    x={margin.left - 10}
                    y={y + 4}
                    textAnchor="end"
                    fill={tickFill}
                    fontSize="11"
                    fontWeight="600"
                    {...svgCommonText}
                  >
                    {tick}
                  </text>
                </g>
              );
            })}

            <line
              x1={margin.left}
              y1={margin.top}
              x2={margin.left}
              y2={margin.top + innerHeight}
              stroke={axisStroke}
              strokeWidth="1"
            />
            <line
              x1={margin.left}
              y1={margin.top + innerHeight}
              x2={margin.left + innerWidth}
              y2={margin.top + innerHeight}
              stroke={axisStroke}
              strokeWidth="1"
            />

            {showSv && hasSv && pathDsv ? (
              <path
                ref={pathSvRef}
                d={pathDsv}
                fill="none"
                stroke={LINE_SV}
                strokeWidth="2.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ) : null}

            {showTl && hasTl && pathDtl ? (
              <path
                ref={pathTlRef}
                d={pathDtl}
                fill="none"
                stroke={LINE_TL}
                strokeWidth="2.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ) : null}

            <g ref={pointWrapRef}>
              {points.map((point) => {
                const tlTitle =
                  showTl && point.tl !== null ? `${labelPrimary} ${point.tl.toFixed(1)}` : null;
                const svTitle =
                  showSv && point.sv !== null ? `${labelSecondary} ${point.sv.toFixed(1)}` : null;
                const titleBits = [point.matchName, tlTitle, svTitle].filter(Boolean).join(' · ');
                return (
                  <g key={point.id}>
                    {showTl && point.tl !== null && (
                      <g className="trend-marker">
                        <circle
                          className="trend-point-circle"
                          cx={point.x}
                          cy={getY(point.tl)}
                          r={3.2}
                          fill={tlPointFill}
                          stroke={markerRing}
                          strokeWidth="1.5"
                          style={{ cursor: 'pointer' }}
                        />
                        <text
                          x={point.x}
                          y={getY(point.tl) - 12}
                          textAnchor="middle"
                          fill={tlValueFill}
                          fontSize="11"
                          fontWeight="700"
                          {...svgCommonText}
                        >
                          {point.tl.toFixed(1)}
                        </text>
                      </g>
                    )}
                    {showSv && point.sv !== null && (
                      <g className="trend-marker">
                        <circle
                          className="trend-sv-circle"
                          cx={point.x}
                          cy={getY(point.sv)}
                          r={3.2}
                          fill={svPointFill}
                          stroke={markerRing}
                          strokeWidth="1.5"
                          style={{ cursor: 'pointer' }}
                        />
                        <text
                          x={point.x}
                          y={getY(point.sv) - 12}
                          textAnchor="middle"
                          fill={svValueLabelFill}
                          fontSize="11"
                          fontWeight="700"
                          {...svgCommonText}
                        >
                          {point.sv.toFixed(1)}
                        </text>
                      </g>
                    )}
                    <text
                      className="trend-marker"
                      x={point.x}
                      y={margin.top + innerHeight + 18}
                      textAnchor="middle"
                      fill={matchLabelFill}
                      fontSize="10"
                      fontWeight="600"
                      letterSpacing="0.02em"
                      {...svgCommonText}
                    >
                      {point.label}
                    </text>
                    <title>{titleBits}</title>
                  </g>
                );
              })}
            </g>
          </svg>
        </div>
      </div>
    </div>
  );
}
